/**
 * rc/components/Component.js
 * 
 * Sistema de componentes: define, c (composición), renderApp
 */

import {
    Signal,
    effect
} from '@core/signal.js'

const components = new Map()

export const define = (name, componentFn, isReactive = false) => {
    if (isReactive) {
        // Envolver la función del componente para hacerlo reactivo
        const reactiveComponentFn = (props = {}) => {
            let element

            // Crear efecto para re-renderizar cuando las props cambien
            effect(() => {
                const newElement = componentFn(props)

                if (element) {
                    // Reemplazar el elemento anterior con el nuevo
                    element.parentNode.replaceChild(newElement, element)
                }

                element = newElement
            })

            return element
        }

        components.set(name, reactiveComponentFn)
    } else {
        components.set(name, componentFn)
    }
}

export const c = (name, props = {}) => {
    const componentFn = components.get(name)
    if (!componentFn) {
        throw new Error(`El componente '${name}' no ha sido definido.`)
    }
    return componentFn(props)
}

export const renderApp = (name, container) => {
    const componentFn = components.get(name)
    if (!componentFn) {
        throw new Error(`El componente '${name}' no ha sido definido.`)
    }

    // Limpiar contenedor
    while (container.firstChild) {
        container.removeChild(container.firstChild)
    }

    // Renderizar componente
    const appElement = componentFn()
    container.appendChild(appElement)

    return appElement
}

export const render = (componentFn, container, props = {}) => {
    let element
    let cleanup

    const update = (newProps) => {
        // Limpiar efecto anterior si existe (effect() devuelve instancia de Effect)
        if (cleanup) cleanup.cleanup()

        // Crear nuevo efecto
        cleanup = effect(() => {
            // <<<--- INICIO DEL ARREGLO ---<<<
            // Guardamos el contexto actual (el de este efecto de render)
            const prev = Signal.current
            // Desconectamos el contexto. Ahora, cuando se ejecute el componente,
            // no habrá un efecto activo al que suscribirse.
            Signal.current = null
            // <<<--- FIN DEL ARREGLO ---<<<

            const newElement = componentFn(newProps)

            // <<<--- RESTAURAMOS EL CONTEXTO ---<<<
            Signal.current = prev
            // <<<--- FIN DE LA RESTAURACIÓN ---<<<

            if (element) {
                // Reemplazar el elemento anterior con el nuevo
                container.replaceChild(newElement, element)
            } else {
                // Añadir elemento inicial
                container.appendChild(newElement)
            }

            element = newElement
        })
    }

    // Render inicial
    update(props)

    return {
        update,
        cleanup: () => {
            if (cleanup) cleanup.cleanup()
            if (element && element.parentNode === container) {
                container.removeChild(element)
            }
        }
    }
}

export const getComponent = (name) => {
    return components.get(name)
}

export const listComponents = () => {
    return Array.from(components.keys())
}